import {
  ComponentProps,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  memo,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { List, type RowComponentProps } from 'react-window'
import { Box } from '@mui/material'
import { ELEMENT_LIST_ROW_HEIGHT } from '../constants'
import { ElementListItem } from '../types'
import ElementRow from './ElementRow'

/**
 * Row data passed straight through react-window's `rowProps`. react-window shallow-compares these
 * values and re-renders the visible rows whenever one changes, so rows never go stale. Keep every
 * value here derived via useMemo/useCallback — never a mutable ref — or the comparison is bypassed.
 */
export interface ElementListRowProps {
  items: ElementListItem[]
  selectedIdSet: Set<string>
  selectable: boolean
  onRowClick: (id: string, event: ReactMouseEvent<HTMLElement>) => void
}

const VirtualizedRow = ({
  index,
  style,
  ariaAttributes,
  items,
  selectedIdSet,
  selectable,
  onRowClick,
}: RowComponentProps<ElementListRowProps>): ReactElement | null => {
  const item = items[index]
  if (!item) return null

  return (
    <div {...ariaAttributes} style={style}>
      <ElementRow
        item={item}
        selected={selectedIdSet.has(item.id)}
        selectable={selectable}
        onClick={event => onRowClick(item.id, event)}
      />
    </div>
  )
}

interface VirtualizedElementListProps {
  listRef?: ComponentProps<typeof List>['listRef']
  rowProps: ElementListRowProps
  onRowsRendered?: ComponentProps<typeof List>['onRowsRendered']
}

const VirtualizedElementList = ({
  listRef,
  rowProps,
  onRowsRendered,
}: VirtualizedElementListProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [listHeight, setListHeight] = useState(0)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateHeight = () => {
      const nextHeight = container.clientHeight
      setListHeight(prev => (prev === nextHeight ? prev : nextHeight))
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        background: 'common.white',
      }}
    >
      {listHeight > 0 ? (
        <List<ElementListRowProps>
          listRef={listRef}
          rowCount={rowProps.items.length}
          rowHeight={ELEMENT_LIST_ROW_HEIGHT}
          rowProps={rowProps}
          rowComponent={VirtualizedRow}
          overscanCount={6}
          defaultHeight={listHeight}
          onRowsRendered={onRowsRendered}
          style={{ height: listHeight, width: '100%' }}
        />
      ) : null}
    </Box>
  )
}

export default memo(VirtualizedElementList)
